"use client";

import React, { useEffect, useState } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import {
  GET_COMPETITION_LADDER,
  GET_COMPETITION_STATS,
} from "@/backend-graphql/queries/backend-graph-queries";
import { useQuery } from "@apollo/client";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import { NoDataDisplay } from "@/components/NoDataDisplay";
import {
  Dropdown,
  MatchCard,
  Tab,
  Table,
} from "@nrldigitaltechnology/ui-community";
import {
  MatchCardBtnType,
  MatchCardDataProps,
} from "@nrldigitaltechnology/ui-community/dist/components/MatchCard/types";
import { TabOptionProps } from "@nrldigitaltechnology/ui-community/dist/components/Tab/types";
import { FieldProps } from "@nrldigitaltechnology/ui-community/dist/components/Table/types";
import { statusMapper } from "@/utils/constants";
import { getS3URL, getNationalId } from "@/utils/utilities";
import { useStateRegionStore } from "@/store/store";
import ImageWithFallback from "@/layouts/ImageFallBack";

export function CompetitionsClient(competitionsProps: any) {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasFilter = searchParams.get("filter");
  const teamSearchFilter = searchParams.get("team");


  const [competition, setCompetition] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState({
    value: "draw",
    label: "Draw",
  });

  const [isLoading, setLoading] = useState<boolean>(false);

  const { competition: competitionState } = useStateRegionStore();

  // Competition Tab
  const [matches, setMatches] = useState<any>([]);
  const [pools, setPools] = useState<any>(null);
  const [selectedPool, setSelectedPool] = useState<any>(null);
  const [teamFilter, setTeamOptions] = useState<any[]>([]);
  const [roundFilter, setRoundOptions] = useState<any[]>([]);
  const [selectedTeam, setTeam] = useState<any>(null);
  const [selectedRound, setRound] = useState<any>(null);
  const [hasInitialRoundSet, setHasInitialRoundSet] = useState<boolean>(false);
  const [selectedPoolLadder, setSelectedPoolLadder] = useState<any>(null);

  // Ladder Tab
  const [ladder, setLadder] = useState<any>([]);

  // Stats Tab
  const [stats, setStats] = useState<any>([]);
  const [selectedStat, setSelectedStat] = useState({
    value: "",
    label: "",
  });
  const [tableKey, updateTableKey] = useState(0);
  const [selectedTeamStat, setTeamStat] = useState<any>(null);
  const nationalId = getNationalId();
  const {
    data: ladderRes,
    loading: ladderLoading,
    error: ladderError,
  } = useQuery(GET_COMPETITION_LADDER, {
    variables: {
      competitionId: Number(params.id),
    },
  });

  const {
    data: statsRes,
    loading: statsLoading,
    error: statsError,
  } = useQuery(GET_COMPETITION_STATS, {
    variables: {
      competitionId: Number(params.id),
      nationalId: nationalId,
    },
  });

  useEffect(() => {
    setLoading(true);
    if (competitionsProps?.data?.matches) {
      const configuredMatches = configureMatches(
        competitionsProps.data.matches
      );
      setMatches(configuredMatches);
    }
    if (competitionsProps?.data?.competition) {
      const { _id, name, orgtree, meta, pools } =
        competitionsProps.data.competition;
      const compObj = {
        _id,
        name,
        orgtree,
        meta,
      };
      setCompetition(compObj);

      if (pools && Array.isArray(pools) && pools.length) {
        const defaultPoolValue = { label: "All", value: "all" };
        const poolArr: TabOptionProps[] = [defaultPoolValue];
        pools.forEach((p) => {
          poolArr.push({
            label: `${p.name}`,
            value: `${p.name}`,
            data: p.teams.map((t: any) => t._id),
          });
        });
        setPools(poolArr);
        setSelectedPool(defaultPoolValue);
        setSelectedPoolLadder(defaultPoolValue);
      }
    }
    setLoading(false);
  }, [competitionsProps]);
  useEffect(() => {
    if (!competitionsProps?.data?.matches) return;
    const filteredMatches = competitionsProps.data.matches.filter((m: any) => {
      const poolCheck =
        pools && selectedPool?.data
          ? selectedPool.data.includes(m?.awayTeam?._id) ||
          selectedPool.data.includes(m?.homeTeam?._id)
          : true;
      const teamCheck = selectedTeam?.value
        ? [m?.awayTeam?._id, m?.homeTeam?._id].includes(selectedTeam.value)
        : true;
      const roundCheck = selectedRound?.label
        ? selectedRound.label === m?.round?.displayName
        : true;
      return teamCheck && roundCheck && poolCheck;
    });
    const configuredMatches = configureMatches(filteredMatches, true);
    setMatches(configuredMatches);
  }, [selectedTeam, selectedRound, selectedPool]);

  useEffect(() => {
    if (!pools || !ladder) return;
    configureLadder(true);
  }, [selectedPoolLadder]);

  useEffect(() => {
    if (roundFilter && roundFilter.length > 0 && !hasFilter && !selectedRound && !hasInitialRoundSet && competitionsProps?.data?.matches) {
      const originalMatches = competitionsProps.data.matches;
      const now = new Date().getTime();
      
      // Find the next upcoming match (earliest future match from today)
      const upcomingMatch = originalMatches
        .filter((m: any) => m.dateTime && new Date(m.dateTime).getTime() >= now)
        .reduce((earliest: any, current: any) => {
          if (!earliest) return current;
          const earliestTime = new Date(earliest.dateTime).getTime();
          const currentTime = new Date(current.dateTime).getTime();
          
          // If same date, prefer Finals over Regular rounds, then higher round number
          if (currentTime === earliestTime && currentTime > 0) {
            const earliestIsFinal = earliest?.round?.type === 'Final';
            const currentIsFinal = current?.round?.type === 'Final';
            
            if (currentIsFinal && !earliestIsFinal) return current;
            if (earliestIsFinal === currentIsFinal && current?.round?.number > earliest?.round?.number) {
              return current;
            }
          }
          
          return currentTime < earliestTime ? current : earliest;
        }, null);

      // If no upcoming matches, find the most recent past match
      const mostRecentMatch = upcomingMatch || originalMatches.reduce((latest: any, current: any) => {
        if (!latest) return current;

        const latestTime = latest.dateTime ? new Date(latest.dateTime).getTime() : 0;
        const currentTime = current.dateTime ? new Date(current.dateTime).getTime() : 0;

        if (currentTime > latestTime) return current;

        if (currentTime === latestTime && currentTime > 0) {
          const latestIsFinal = latest?.round?.type === 'Final';
          const currentIsFinal = current?.round?.type === 'Final';

          if (currentIsFinal && !latestIsFinal) return current;
          if (latestIsFinal === currentIsFinal && current?.round?.number > latest?.round?.number) {
            return current;
          }
        }

        return latest;
      }, null);

      if (mostRecentMatch?.round?.displayName) {
        const defaultRound = roundFilter.find(
          (r) => r.label === mostRecentMatch.round.displayName
        );
        if (defaultRound) {
          setRound(defaultRound);
          setHasInitialRoundSet(true);
        }
      }
    } else if (hasFilter) {
      switch (hasFilter) {
        case "teams":
          const teamId = Number(teamSearchFilter);
          const foundTeam = teamFilter.find((r) => r.value === teamId);
          if (foundTeam) setTeam(foundTeam);
          break;
      }
    }

  }, [roundFilter, competitionsProps?.data?.matches, hasFilter]);

  useEffect(() => {
    if (selectedRound && roundFilter && roundFilter.length > 0) {
      const isRoundAvailable = roundFilter.some(
        (r: any) => r.label === selectedRound.label && r.value === selectedRound.value
      );
      if (!isRoundAvailable) {
        if (competitionsProps?.data?.matches) {
          const filteredMatches = competitionsProps.data.matches.filter((m: any) => {
            const poolCheck =
              pools && selectedPool?.data
                ? selectedPool.data.includes(m?.awayTeam?._id) ||
                selectedPool.data.includes(m?.homeTeam?._id)
                : true;
            const teamCheck = selectedTeam?.value
              ? [m?.awayTeam?._id, m?.homeTeam?._id].includes(selectedTeam.value)
              : true;
            return teamCheck && poolCheck;
          });

          const now = new Date().getTime();
          
          const upcomingMatch = filteredMatches
            .filter((m: any) => m.dateTime && new Date(m.dateTime).getTime() >= now)
            .reduce((earliest: any, current: any) => {
              if (!earliest) return current;
              const earliestTime = new Date(earliest.dateTime).getTime();
              const currentTime = new Date(current.dateTime).getTime();
              
              if (currentTime === earliestTime && currentTime > 0) {
                const earliestIsFinal = earliest?.round?.type === 'Final';
                const currentIsFinal = current?.round?.type === 'Final';
                
                if (currentIsFinal && !earliestIsFinal) return current;
                if (earliestIsFinal === currentIsFinal && current?.round?.number > earliest?.round?.number) {
                  return current;
                }
              }
              
              return currentTime < earliestTime ? current : earliest;
            }, null);


          const mostRecentMatch = upcomingMatch || filteredMatches.reduce((latest: any, current: any) => {
            if (!latest) return current;
            const latestTime = latest.dateTime ? new Date(latest.dateTime).getTime() : 0;
            const currentTime = current.dateTime ? new Date(current.dateTime).getTime() : 0;
            if (currentTime > latestTime) return current;
            if (currentTime === latestTime && currentTime > 0) {
              const latestIsFinal = latest?.round?.type === 'Final';
              const currentIsFinal = current?.round?.type === 'Final';
              if (currentIsFinal && !latestIsFinal) return current;
              if (latestIsFinal === currentIsFinal && current?.round?.number > latest?.round?.number) {
                return current;
              }
            }
            return latest;
          }, null);

          if (mostRecentMatch?.round?.displayName) {
            const defaultRound = roundFilter.find(
              (r) => r.label === mostRecentMatch.round.displayName
            );
            if (defaultRound) {
              setRound(defaultRound);
            } else {
              setRound(null);
            }
          } else {
            setRound(null);
          }
        } else {
          setRound(null);
        }
      }
    }
  }, [roundFilter, selectedPool, selectedTeam, pools, competitionsProps?.data?.matches]);

  useEffect(() => {
    configureLadder();
  }, [ladderLoading]);

  useEffect(() => {
    if (statsLoading || !statsRes?.playerstats || statsError) return;
    if (statsRes?.playerstats && Array.isArray(statsRes.playerstats)) {
      configureStats(statsRes.playerstats);
      setSelectedStat({
        value: "tries",
        label: "Tries",
      });
    }
  }, [statsLoading]);

  useEffect(() => {
    const lastCol = statsFields.length - 1;
    const statsCol = {
      name: selectedStat.value,
      label: selectedStat.label,
      headerStyle:
        "w-[85px] min-w-[85px] max-w-[85px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px] sticky left-[0px] md:static z-10 pl-3 bg-[#F6F6F6] text-xs sm:text-sm",
      cellStyle:
        "w-[85px] min-w-[85px] max-w-[85px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px] sticky left-[0px] md:static z-10 pl-4 bg-white",
      formatter: (value: any) => {
        return <LadderPoints value={value} />;
      },
    };
    statsFields[lastCol] = statsCol;
    const sortedStats = stats
      .sort((a: any, b: any) => {
        return b[selectedStat.value] - a[selectedStat.value];
      })
      .map((p: any, i: number) => ({
        pos: i + 1,
        logo: p.logo,
        player: p.player,
        team: p.team,
        played: p.played,
        points: p.points,
        tries: p.tries,
      }));
    setStats(sortedStats);
    updateTableKey(Date.now());
  }, [selectedStat]);

  useEffect((): any => {
    if (!selectedTeamStat)
      return statsRes?.playerstats
        ? configureStats(statsRes?.playerstats)
        : undefined;
    const filteredStats = statsRes?.playerstats.filter(
      (s: any) => s?.team?._id === selectedTeamStat?.value
    );
    configureStats(filteredStats);
  }, [selectedTeamStat]);

  const onChangeTab = (item: TabOptionProps, tab: string) => {
    switch (tab) {
      case "comp":
        setSelectedTab(item);
        break;
      case "pool":
        setSelectedPool(item);
        break;
      case "poolLadder":
        setSelectedPoolLadder(item);
        break;
      case "stats":
        setSelectedStat(item);
        break;
    }
  };
  const configureMatches = (matches: any, skipFilterValueReset?: boolean) => {
    const uniqueTeams = new Set();
    const uniqueRoundsMap = new Map<string, { number: number; displayName: string }>();
    const uniqueFinalRoundsMap = new Map<string, { number: number; displayName: string }>();

    const configuredMatches = matches
      .sort((a: any, b: any) => {
        if (!a?.dateTime) return 1;
        if (!b?.dateTime) return -1;
        if (a.dateTime === b.dateTime) {
          if (a?.round?.type === "Regular" && b?.round?.type === "Final")
            return -1;
          else if (a?.round?.type === "Final" && b?.round?.type === "Regular")
            return 1;
          else return a?.round?.number - b?.round?.number;
        }
        return a.dateTime - b.dateTime;
      })
      .map((m: any, i: any) => {
        if (m.awayTeam) uniqueTeams.add(m.awayTeam?._id);
        if (m.homeTeam) uniqueTeams.add(m.homeTeam?._id);
        if (m?.round?.type === "Regular" && m?.round?.number) {
          const displayName = m?.round?.displayName || `Round ${m.round.number}`;
          const key = displayName.toLowerCase().trim();
          if (!uniqueRoundsMap.has(key)) {
            uniqueRoundsMap.set(key, {
              number: m.round.number,
              displayName: displayName,
            });
          }
        } else if (
          m?.round?.type === "Final" &&
          m?.round?.displayName &&
          m?.round?.number
        ) {
          const displayName = m.round.displayName;
          const key = displayName.toLowerCase().trim();
          if (!uniqueFinalRoundsMap.has(key)) {
            uniqueFinalRoundsMap.set(key, {
              number: m.round.number,
              displayName: displayName,
            });
          }
        }

        let matchStatus: { id: string; label: string; shortLabel?: string } = {
          id: "",
          label: "",
        };

        const statusStr = m?.status?.toLowerCase();

        switch (statusStr) {
          case "final":
            matchStatus = {
              id: "fulltime",
              label: "FULLTIME",
              shortLabel: "F/T",
            };
            break;
          case "forfeit":
          // case "pre-game":
          case "postponed":
          case "washout":
            matchStatus =
              statusMapper.find((s) => s.id === statusStr) || matchStatus;
            break;
          default:
            if (m?.meta?.isTba)
              matchStatus =
                statusMapper.find((s) => s.id === "tba") || matchStatus;
            else if (statusStr === "pre-game" || statusStr === "in-progress") {
              const gameStateForMatch =
                m && m.gameStates && m.gameStates.length ? m.gameStates : [];
              if (gameStateForMatch.length > 0) {
                const lastElement =
                  gameStateForMatch[gameStateForMatch.length - 1] || "";
                if (
                  typeof lastElement === "string" &&
                  (lastElement.toLowerCase().includes("Period") ||
                    lastElement.toLowerCase().includes("First Half") ||
                    lastElement.toLowerCase().includes("Extra Time"))
                )
                  matchStatus =
                    statusMapper.find((s) => s.id === "live") || matchStatus;
              }
            } else {
              matchStatus = { label: "", id: "" };
            }
            break;
        }

        return {
          id: m._id,
          round: m?.round?.displayName,
          homeTeam: {
            id: m?.homeTeam?._id,
            name: m?.homeTeam?.name,
            score: m?.scores?.homeTeam,
            logo: m?.homeTeam?.avatar
              ? m.homeTeam.avatar
              : m?.meta?.isTba
                ? "/assets/logo/noimage_large.png"
                :
                getS3URL(
                  m?.homeTeam?.orgtree?.club?._id ||
                  competition?.orgtree?.association?._id ||
                  0
                ) || "/assets/logo/noimage_large.png",
            forfeit:
              m.status === "forfeit" &&
              m?.meta?.forfeitingTeam?._id &&
              m.meta.forfeitingTeam._id === m?.homeTeam?._id,
          },
          awayTeam: {
            id: m?.awayTeam?._id,
            name: m?.awayTeam?.name,
            score: m?.scores?.awayTeam,
            logo: m?.awayTeam?.avatar
              ? m.awayTeam.avatar
              : m?.meta?.isTba
                ? "/assets/logo/noimage_large.png"
                : getS3URL(
                  m?.awayTeam?.orgtree?.club?._id ||
                  competition?.orgtree?.association?._id ||
                  0
                ) || "/assets/logo/noimage_large.png",
            forfeit:
              m.status === "forfeit" &&
              m?.meta?.forfeitingTeam?._id &&
              m.meta.forfeitingTeam._id === m?.awayTeam?._id,
          },
          status: matchStatus,
          liveUrl: m?.meta?.liveUrl || "",
          venue: m?.venue?.name
            ? `${m.venue.name}${m?.meta?.fieldNo ? " - " + m.meta.fieldNo : ""}`
            : undefined,
          dateTime: m?.dateTime ? new Date(m.dateTime) : "",
          byes: m?.meta?.isBye,
          period: (!m?.meta?.liveUrl && m?.status) || "",
          customStyles: {
            containerStyle: `flex flex-col ${!m?.meta?.isBye ? "justify-between" : ""
              }`,
            dateContainer: "w-auto ",
            logoContainer: "w-auto h-auto sm:w-auto sm:h-auto",
            teamContainer:
              "max-w-none mx-[6px] sm:mx-2 w-auto sm:w-auto md:w-auto lg:w-auto xl:w-auto",
            teamLabels: `text-[10px] sm:text-sm md:text-xs lg:text-[12px] xl:text-sm ${!m?.meta?.isBye ? "sm:w-[150px] w-[65px]" : ""
              }`,
            teamLogos:
              "md:w-[65px] md:h-[65px]  w-[40px] h-[40px] object-contain rounded-md",
            timeLabel: "sm:text-base",
          },
          alternateSmMob: true,
        };
      });

    if (!skipFilterValueReset) {
      const teamOptions = [...uniqueTeams]
        .filter((c) => !!c)
        .map((t: any) => {
          const matchingValue = competitionsProps.data?.matches.find(
            (m: any) => t === m?.awayTeam?._id || t === m?.homeTeam?._id
          );
          return matchingValue?.awayTeam?._id === t
            ? {
              value: matchingValue.awayTeam._id,
              label: matchingValue.awayTeam.name,
            }
            : matchingValue?.homeTeam?._id === t
              ? {
                value: matchingValue?.homeTeam?._id,
                label: matchingValue?.homeTeam?.name,
              }
              : false;
        })
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
      setTeamOptions(teamOptions);
    }

    const roundRegularOptions = Array.from(uniqueRoundsMap.values())
      .sort((a, b) => {
        if (a.number !== b.number) return a.number - b.number;
        return a.displayName.localeCompare(b.displayName);
      })
      .map((r) => ({
        label: r.displayName,
        value: `${r.number}_regular`
      }));
    const roundFinalOptions = Array.from(uniqueFinalRoundsMap.values())
      .sort((a, b) => {
        if (a.number !== b.number) return a.number - b.number;
        return a.displayName.localeCompare(b.displayName);
      })
      .map((r) => ({
        label: r.displayName,
        value: `${r.number}_${r.displayName}`
      }));

    const allRoundOptions = [...roundRegularOptions, ...roundFinalOptions];
    const uniqueRoundOptions = Array.from(
      new Map(
        allRoundOptions.map((option) => [
          option.label.toLowerCase().trim(),
          option,
        ])
      ).values()
    );
    setRoundOptions(uniqueRoundOptions);

    return configuredMatches;
  };
  const configureStats = (stats: any) => {
    if (!stats) return;
    const statsData = stats.map((p: any, i: number) => ({
      pos: i + 1,
      logo: p?.team?.logo
        ? p.team.logo
        : getS3URL(p?.team?.orgtree?.club?._id) || "/assets/logo/noimage.png",
      player: p?.player?.isPrivate
        ? "Name Private"
        : `${p?.player?.firstName} ${p?.player?.lastName}`,
      team: p?.team?.name,
      played: p?.games,
      points: p?.totalPoints,
      tries: p?.tries,
    }));
    statsData.sort((statA: any, statB: any) => {
      if (selectedStat?.value === "tries") return statB.tries - statA.tries;
      if (selectedStat?.value === "points") return statB.points - statA.points;
      return 1;
    });
    setStats(statsData);
  };
  const configureLadder = (filter = false) => {
    if (ladderLoading || !ladderRes?.competitionLadder || ladderError) return;
    if (
      ladderRes?.competitionLadder?.teams &&
      Array.isArray(ladderRes.competitionLadder.teams)
    ) {
      const ladderArray = filter
        ? ladderRes.competitionLadder.teams.filter((team: any) => {
          if (
            !selectedPoolLadder?.value ||
            selectedPoolLadder.value === "all"
          )
            return true;
          return (
            selectedPoolLadder?.data &&
            Array.isArray(selectedPoolLadder.data) &&
            selectedPoolLadder.data.includes(team._id)
          );
        })
        : ladderRes.competitionLadder.teams;
      const ladderData = ladderArray.map((team: any, i: number) => {
        const { stats } = team;
        return {
          pos: i + 1,
          logo: team?.avatar || "/assets/logo/noimage.png",
          team: team?.name,
          played: stats?.matchesPlayed,
          points: stats?.totalMatchPoints,
          wins: stats?.matchesWon,
          drawn: stats?.matchesDrawn,
          lost: stats?.matchesLost,
          byes: stats?.byes,
          for: stats?.pointsFor,
          against: stats?.pointsAgainst,
          diff: stats?.pointsDifference,
          ratio: stats?.matchWinRatio,
        };
      });
      setLadder(ladderData);
    }
  };
  const matchClick = (
    cardData: MatchCardDataProps | undefined,
    cardType: MatchCardBtnType
  ) => {
    if (!cardData) return;
    const currentMatch = competitionsProps.data.matches.find(
      (m: any) => m._id === cardData?.id
    );
    const { meta } = currentMatch;
    if (cardType === "primary" && currentMatch?.meta?.liveUrl) {
      const { meta } = currentMatch;
      if (meta?.liveUrl) {
        let url = meta.liveUrl;
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        window.open(url, "_blank")?.focus();
      }
    } else if (currentMatch?.fullVenue) {
      const { address } = currentMatch.fullVenue;

      let queryStr = `query=${cardData?.venue?.replace(/\s+/g, "+")}`;
      if (address.formatted)
        queryStr = queryStr + `,${address.formatted.replace(/\s+/g, "+")}`;

      const mapsStr = `https://www.google.com/maps/search/?api=1&${queryStr}`;
      window.open(mapsStr, "_blank")?.focus();
    }
  };

  const handleOnClickShare = async (cardData: MatchCardDataProps) => {
    const currentUrl = `${window.location.origin}/competitions/match/${cardData.id}?type=match`
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="flex w-full flex-col items-center bg-[#F8F8F8]">
      <div className="w-[100%]  flex flex-col my-10">
        <div className="flex items-center gap-4">
          {
            competitionState?.label &&
            <div className="w-[60px] h-[60px] rounded-md bg-white p-[8px]">
              <ImageWithFallback
                src={competitionState?.logo ||
                  getS3URL(competition?.orgtree?.association?._id || 0)}
                alt="Competition Logo"
                style="object-contain w-full h-full" fallbackSrc={"/assets/logo/noimage.png"} />
            </div>
          }
          <span className="font-base/[30px] font-medium uppercase tracking-[1.2px]">
            {competitionState?.label}
          </span>
        </div>
        <Tab
          options={[
            {
              value: "draw",
              label: "Draw",
            },
            {
              value: "ladder",
              label: "Ladder",
            },
            {
              value: "stats",
              label: "Stats",
            },
          ]}
          tab={selectedTab}
          onChangeTab={(item) => onChangeTab(item, "comp")}
          variant="underline"
          customStyles={{
            containerStyle:
              "bg-white rounded-t-md border-b border-b-[#D2D2D2] px-[16px] overflow-x-auto overflow-y-clip mt-6",
            tabBtnStyle:
              "data-[selected=true]:border-b-[3px] mb-[-1px] py-[10px] px-[16px]",
          }}
        />
        {selectedTab.value === "draw" && (
          <div className="flex lg:flex-row flex-col gap-2 justify-between w-full bg-white rounded-b-md px-[24px] py-[16px]">
            {pools && (
              <Tab
                options={pools}
                tab={selectedPool}
                onChangeTab={(item) => onChangeTab(item, "pool")}
                variant="tabbed"
                customStyles={{
                  containerStyle: "flex-col sm:flex-row",
                }}
              />
            )}
            <div className="flex flex-col gap-2 lg:flex-row">
              <Dropdown
                placeHolder="All Teams"
                options={teamFilter}
                onSelect={(v) => {
                  setTeam(v);
                }}
                selectedOption={selectedTeam}
                customStyles={{
                  containerStyle: "lg:w-[200px]",
                }}
              />
              <Dropdown
                placeHolder="Round"
                options={roundFilter}
                onSelect={(v) => {
                  setRound(v);
                  setHasInitialRoundSet(true);
                }}
                selectedOption={selectedRound}
                customStyles={{
                  containerStyle: "lg:w-[200px]",
                }}
              />
            </div>
          </div>
        )}
        {selectedTab.value === "draw" &&
          (!isLoading ? (
            Array.isArray(matches) && matches.length ? (
              <React.Fragment>
                <div className="grid grid-cols-1 gap-2 2xl:gap-4 mt-6 lg:grid-cols-2 overflow-x-hidden">
                  {matches.map(
                    (cardData: MatchCardDataProps, index: number) => (
                      <div key={`${cardData.id}_${index}`} className="relative overflow-visible min-w-0">
                        <MatchCard
                        
                          shareIconHover={
                            <div className="text-white flex justify-center text-[12px] items-center w-[90px] bg-black/80 rounded-md px-2 py-1">
                              Copy Link
                            </div>
                            }
                          shareIconCopied={
                            <div className="bg-black/70 px-3 py-1 rounded-md font-regular text-white text-sm">
                              Copied to Clipboard
                            </div>
                          }

                          cardData={cardData}
                          onClickCard={() =>
                            router.push(
                              `/competitions/match/${cardData.id}?type=match`
                            )
                          }
                          onClickVenue={(data, type) => matchClick(data, type)}
                          onClickShare={() =>
                             handleOnClickShare(cardData)
                          }
                        />
                      </div>
                    )
                  )}
                </div>
              </React.Fragment>
            ) : (
              <NoDataDisplay dataSearch="matches" customStyle="mt-6" />
            )
          ) : (
            <LoadingSpinner />
          ))}
        {selectedTab.value === "ladder" && (
          <React.Fragment>
            {ladderLoading ? (
              <LoadingSpinner />
            ) : ladderRes?.competitionLadder && ladder ? (
              <React.Fragment>
                <div className="flex lg:flex-row flex-col gap-2 justify-between w-full bg-white rounded-b-md px-[24px] py-[16px]">
                  {pools && (
                    <Tab
                      options={pools}
                      tab={selectedPoolLadder}
                      onChangeTab={(item) => onChangeTab(item, "poolLadder")}
                      variant="tabbed"
                      customStyles={{
                        containerStyle: "flex-col sm:flex-row",
                      }}
                    />
                  )}
                </div>
                <Table
                  data={ladder}
                  fields={fields}
                  enablePagination={false}
                  className="bg-white mt-[14px]"
                />
              </React.Fragment>
            ) : ladderError ? (
              JSON.stringify(ladderError)
            ) : (
              <NoDataDisplay
                dataSearch="ladders"
                message="Sorry, it looks like your ladder has been hidden. Please note ladders may not be available for non-competition age groups."
                customStyle="mt-6"
              />
            )}
          </React.Fragment>
        )}
        {selectedTab.value === "stats" && (
          <React.Fragment>
            {statsLoading ? (
              <LoadingSpinner />
            ) : statsRes?.playerstats && stats ? (
              <>
                <div className="flex lg:flex-row flex-col gap-2 justify-between w-full bg-white rounded-b-md px-[24px] py-[16px]">
                  {process.env.NEXT_PUBLIC_APP_SLUG &&
                    process.env.NEXT_PUBLIC_APP_SLUG === "tfa" ? (
                    <Tab
                      options={[{ value: "tries", label: "Tries" }]}
                      tab={selectedStat}
                      onChangeTab={(item) => onChangeTab(item, "stats")}
                      variant="tabbed"
                      customStyles={{ tabBtnStyle: "w-full min-w-fit" }}
                    />
                  ) : (
                    <Tab
                      options={[
                        { value: "tries", label: "Tries" },
                        { value: "points", label: "Total Points" },
                      ]}
                      tab={selectedStat}
                      onChangeTab={(item) => onChangeTab(item, "stats")}
                      variant="tabbed"
                      customStyles={{ tabBtnStyle: "w-full min-w-fit" }}
                    />
                  )}
                  <div className="flex flex-col gap-2 lg:flex-row">
                    <Dropdown
                      placeHolder="All Teams"
                      options={teamFilter}
                      onSelect={(v) => {
                        setTeamStat(v);
                      }}
                      selectedOption={selectedTeamStat}
                      customStyles={{
                        containerStyle: "lg:w-[200px]",
                        optionsContainerStyle: "z-[15]",
                      }}
                    />
                  </div>
                </div>
                {stats?.length ? (
                  <div key={tableKey}>
                    <Table
                      data={stats}
                      fields={statsFields}
                      enablePagination={false}
                      custNoRecordStyle={{
                        label:
                          "Looks like your League is still crunching the numbers. Check back soon for updates but please note stats may not be available for non-competition age groups.",
                      }}
                      className="bg-white mt-[14px]"
                    />
                  </div>
                ) : (
                  <NoDataDisplay
                    dataSearch="stats"
                    message="Looks like your League is still crunching the numbers. Check back soon for updates but please note stats may not be available for non-competition age groups."
                    customStyle="mt-6"
                  />
                )}
              </>
            ) : statsError ? (
              JSON.stringify(statsError)
            ) : null}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

const LadderPoints = ({ value }: { value: string }) => {
  return (
    <div className="flex items-center justify-start h-10 ">
      <p className="text-black">{value}</p>
    </div>
  );
};

const fields: FieldProps<any>[] = [
  {
    name: "pos",
    label: "Pos",
    headerStyle:
      "w-[35px] min-w-[35px] max-w-[35px] sm:w-[50px] sm:min-w-[50px] sm:max-w-[50px] sticky left-[0px] md:static z-10 pl-3 rounded-l-lg bg-[#F6F6F6]",
    cellStyle:
      "w-[35px] min-w-[35px] max-w-[35px] sm:w-[50px] sm:min-w-[50px] sm:max-w-[50px] sticky left-[0px] md:static z-10 pl-4 bg-white",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "team",
    label: "Team",
    headerStyle:
      "sticky min-w-[150px] left-[35px] sm:left-[50px] md:static z-10 pl-3 pr-3 bg-[#F6F6F6] ",
    cellStyle:
      "sticky left-[35px] sm:left-[50px] md:static z-10 pl-4 pr-3 bg-white",

    type: "object",
    formatter: (value: { team: string; logo: string }) => {
      const typedValue = value;

      return (
        <div className="flex px-1 justify-start items-center gap-2 md:gap-5 shadow-[6px_0px_8px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
          <div className="flex items-center justify-center my-1 rounded-lg md:border md:border-gray-200 shrink-0 w-14 h-14 md:w-16 lg:h-16">
            <img className="w-9 h-9 md:w-11 lg:h-11" src={value.logo} alt=" " />
          </div>
          <div className="">
            <p>{typedValue.team}</p>
          </div>
        </div>
      );
    },
  },
  {
    name: "played",
    label: "Played",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "points",
    label: "Points",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "wins",
    label: "Wins",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "drawn",
    label: "Drawn",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "lost",
    label: "Lost",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "byes",
    label: "Byes",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "for",
    label: "For",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "against",
    label: "Against",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "diff",
    label: "Diff",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "ratio",
    label: "Ratio",
    headerStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    cellStyle:
      "w-[50px] min-w-[50px] max-w-[50px] sm:w-auto sm:min-w-[60px] sm:max-w-auto",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
];

const statsFields: FieldProps<any>[] = [
  {
    name: "pos",
    label: "Pos",
    headerStyle:
      "w-[45px] min-w-[45px] max-w-[45px] sm:w-[75px] sm:min-w-[75px] sm:max-w-[75px] sticky left-[0px] md:static z-10 pl-3 rounded-l-lg bg-[#F6F6F6] text-xs sm:text-sm",
    cellStyle:
      "w-[45px] min-w-[45px] max-w-[45px] sm:w-[75px] sm:min-w-[75px] sm:max-w-[75px] sticky left-[0px] md:static z-10 pl-4 bg-white",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "team",
    label: "Team",
    headerStyle:
      "sticky min-w-[150px] left-[45px] sm:left-[75px] md:static z-10 pl-3 pr-3 bg-[#F6F6F6] text-xs sm:text-sm",
    cellStyle:
      "sticky left-[45px] sm:left-[75px] md:static z-10 sm:pl-4 pl-0 pr-3 bg-white",

    type: "object",
    formatter: (value: { player: string; team: string; logo: string }) => {
      const typedValue = value;
      return (
        <div className="flex px-1 justify-start items-center gap-2 md:gap-5 shadow-[6px_0px_8px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
          <div className="flex items-center justify-center my-1 rounded-lg md:border md:border-gray-200 shrink-0 w-14 h-14 md:w-16 lg:h-16">
            <img className="w-9 h-9 md:w-11 lg:h-11" src={value.logo} alt=" " />
          </div>
          <div className="flex flex-col">
            <span className="text-xs sm:text-sm">{typedValue.player}</span>
            <span className="sm:text-xs text-[10px] font-normal">
              {typedValue.team}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    name: "played",
    label: "Played",
    headerStyle:
      "w-[60px] min-w-[60px] max-w-[60px] sm:w-[75px] sm:min-w-[75px] sm:max-w-[75px] sticky left-[0px] md:static z-10 pl-3 bg-[#F6F6F6] text-xs sm:text-sm",
    cellStyle:
      "w-[60px] min-w-[60px] max-w-[60px] sm:w-[75px] sm:min-w-[75px] sm:max-w-[75px] sticky left-[0px] md:static z-10 pl-4 bg-white",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
  {
    name: "tries",
    label: "Tries",
    headerStyle:
      "w-[85px] min-w-[85px] max-w-[85px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px] sticky left-[0px] md:static z-10 pl-3 bg-[#F6F6F6] text-xs sm:text-sm",
    cellStyle:
      "w-[85px] min-w-[85px] max-w-[85px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px] sticky left-[0px] md:static z-10 pl-4 bg-white",
    formatter: (value) => {
      return <LadderPoints value={value} />;
    },
  },
];
